import {useEffect, useState} from 'react';
import {getReportHeadersByIds, subscribeToUserDisclosures} from '../../../firebase/firestore/reports';

function enrichDisclosuresWithParentTitles(reports, headersById) {
    return (reports || []).map((report) => {
        const parentId = report.parentReportId
            || report.meta?.parentReportId
            || report.parent_product?.uid
            || report.meta?.parent_product?.uid
            || null;
        const parentTitle = report.parent_product?.title
            || report.meta?.parent_product?.title
            || (parentId && headersById[parentId]?.title)
            || null;
        return {
            ...report,
            parentReportId: parentId,
            parentTitle,
        };
    });
}

/**
 * Custom hook to subscribe to user disclosure reports.
 * @param {string} userId - The user ID to fetch reports for
 * @param {Object} options
 * @param {boolean} options.adminView - If true, fetch all disclosures regardless of status
 */
export const useUserDisclosures = (userId, {adminView = false} = {}) => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const unsubscribe = subscribeToUserDisclosures(
            userId,
            async (data) => {
                try {
                    const rows = Array.isArray(data) ? data : [];
                    const parentIds = rows
                        .map((r) => r.parentReportId || r.meta?.parentReportId
                            || r.parent_product?.uid || r.meta?.parent_product?.uid)
                        .filter(Boolean);
                    const headersById = parentIds.length
                        ? await getReportHeadersByIds(parentIds)
                        : {};
                    setReports(enrichDisclosuresWithParentTitles(rows, headersById));
                    setError(null);
                } catch (err) {
                    console.error('Failed to enrich disclosure parent FINTEL titles', err);
                    setReports(Array.isArray(data) ? data : []);
                    setError(err);
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                console.error('Failed to subscribe to disclosure reports', err);
                setError(err);
                setLoading(false);
            },
            {adminView}
        );

        return () => {
            try {
                unsubscribe && unsubscribe();
            } catch (e) {
                // Cleanup error - safe to ignore
            }
        };
    }, [userId, adminView]);

    const removeReport = (reportId) =>
        setReports(prev => prev.filter(r => r.id !== reportId));

    return {reports, loading, error, removeReport};
};
